import { useState } from 'react'
import type { FormEvent } from 'react'
import { api, ApiError } from '../lib/api'
import type { DraftResponse, DraftState, DraftType, ExpiryPolicy } from '../types/protocol'

interface Props {
  draft: DraftState
  commissionerToken: string
  onDraft: (draft: DraftState) => void
}

const EXPIRY_LABELS: Record<ExpiryPolicy, string> = {
  autopick: 'Autopick best available',
  skip: 'Skip the pick',
  wait: 'Wait for the pick',
}

export default function SettingsForm({ draft, commissionerToken, onDraft }: Props) {
  const [name, setName] = useState(draft.name)
  const [draftType, setDraftType] = useState<DraftType>(draft.draft_type)
  const [pickSeconds, setPickSeconds] = useState(String(draft.pick_seconds))
  const [expiryPolicy, setExpiryPolicy] = useState<ExpiryPolicy>(draft.expiry_policy)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const locked = draft.status !== 'setup'

  async function save(event: FormEvent) {
    event.preventDefault()
    setSaving(true)
    setSaved(false)
    setError(null)
    try {
      const response = await api<DraftResponse>(`/drafts/${draft.join_code}/settings`, {
        method: 'PATCH',
        commissionerToken,
        body: {
          name: name.trim(),
          draft_type: draftType,
          pick_seconds: Number(pickSeconds),
          expiry_policy: expiryPolicy,
        },
      })
      onDraft(response.draft)
      setSaved(true)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="panel p-6">
      <h2 className="eyebrow">Settings</h2>
      <form onSubmit={(event) => void save(event)} className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm text-ink-mid sm:col-span-2">
          Draft name
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={locked}
            className="rounded border border-line-strong bg-ink px-3 py-2 text-ink-hi"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-ink-mid">
          Draft type
          <select
            value={draftType}
            onChange={(event) => setDraftType(event.target.value as DraftType)}
            disabled={locked}
            className="rounded border border-line-strong bg-ink px-3 py-2 text-ink-hi"
          >
            <option value="snake">Snake</option>
            <option value="linear">Linear</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm text-ink-mid">
          Pick clock (seconds, 10–600)
          <input
            type="number"
            min={10}
            max={600}
            value={pickSeconds}
            onChange={(event) => setPickSeconds(event.target.value)}
            disabled={locked}
            className="rounded border border-line-strong bg-ink px-3 py-2 text-ink-hi"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-ink-mid sm:col-span-2">
          When the clock runs out
          <select
            value={expiryPolicy}
            onChange={(event) => setExpiryPolicy(event.target.value as ExpiryPolicy)}
            disabled={locked}
            className="rounded border border-line-strong bg-ink px-3 py-2 text-ink-hi"
          >
            {(Object.keys(EXPIRY_LABELS) as ExpiryPolicy[]).map((policy) => (
              <option key={policy} value={policy}>
                {EXPIRY_LABELS[policy]}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-3 sm:col-span-2">
          <button
            type="submit"
            disabled={saving || locked}
            className="rounded bg-signal px-4 py-2 text-sm font-semibold text-ink hover:bg-signal-dim disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save settings'}
          </button>
          {saved && <span className="text-sm text-signal">Saved</span>}
          {error && <span className="text-sm text-crit">{error}</span>}
        </div>
      </form>
    </section>
  )
}
